import React, { useState, useRef, useEffect, useCallback } from 'react';
import { HexColorPicker } from 'react-colorful';

const WindowLens = ({ theme = 'light' }) => {
  // Layer system
  const [layers, setLayers] = useState([
    { id: 'bg', name: 'Background', image: null, visible: true, locked: false },
    { id: 'fg', name: 'Foreground', image: null, visible: true, locked: false },
  ]);
  const [activeLayer, setActiveLayer] = useState('fg');

  // Keying settings
  const [keyColor, setKeyColor] = useState('#00ff00');
  const [screenGain, setScreenGain] = useState(100);
  const [screenBalance, setScreenBalance] = useState(50);
  const [clipBlack, setClipBlack] = useState(5);
  const [clipWhite, setClipWhite] = useState(95);
  const [despillBias, setDespillBias] = useState(70);
  const [edgeSoften, setEdgeSoften] = useState(3);
  const [edgeShrink, setEdgeShrink] = useState(0);
  
  // Glass effect settings
  const [replaceColor, setReplaceColor] = useState('#d0d0d0');
  const [glassOpacity, setGlassOpacity] = useState(65);
  const [gradientStrength, setGradientStrength] = useState(50);
  const [reflectionOpacity, setReflectionOpacity] = useState(25);
  const [glassTint, setGlassTint] = useState(0);
  const [noiseAmount, setNoiseAmount] = useState(15);
  const [noiseScale, setNoiseScale] = useState(50);
  
  // Eyedropper state
  const [eyedropperActive, setEyedropperActive] = useState(false);
  const [sampledColor, setSampledColor] = useState(null);
  const [eyedropperPosition, setEyedropperPosition] = useState({ x: 0, y: 0 });

  // Secondary image controls
  const [bgScale, setBgScale] = useState(100);
  const [bgRotation, setBgRotation] = useState(0);
  const [bgFlipH, setBgFlipH] = useState(false);
  const [bgFlipV, setBgFlipV] = useState(false);
  const [bgPositionX, setBgPositionX] = useState(50);
  const [bgPositionY, setBgPositionY] = useState(50);
  const [bgPerspective, setBgPerspective] = useState(0);
  const [bgOpacity, setBgOpacity] = useState(100);
  const [bgSkewX, setBgSkewX] = useState(0);
  const [bgSkewY, setBgSkewY] = useState(0);

  // Transform tool state
  const [transformMode, setTransformMode] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isScaling, setIsScaling] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialTransform, setInitialTransform] = useState({});
  const [transformHandle, setTransformHandle] = useState(null);

  // UI state
  const [showKeyColorPicker, setShowKeyColorPicker] = useState(false);
  const [showReplaceColorPicker, setShowReplaceColorPicker] = useState(false);
  const [showProperties, setShowProperties] = useState(true);
  const [activePanel, setActivePanel] = useState('keying');
  const [isProcessing, setIsProcessing] = useState(false);

  // Refs
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const bgFileInputRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const previewContainerRef = useRef(null);
  const keyColorPickerRef = useRef(null);
  const replaceColorPickerRef = useRef(null);

  const themeColors = {
    light: {
      bg: '#FAFAFA',
      surface: '#FFFFFF',
      border: '#E8E8E8',
      text: '#333',
      textSecondary: '#999',
      accent: '#88B7FF',
      accentHover: '#5998FF',
      canvas: '#E8E8E8',
      buttonBg: '#F5F5F5',
      inputBg: '#FFFFFF',
      danger: '#FF6B6B',
    },
    dark: {
      bg: '#2c2c2c',
      surface: '#333333',
      border: '#555',
      text: '#fff',
      textSecondary: '#aaa',
      accent: '#4594f9',
      accentHover: '#3a7ed4',
      canvas: '#1a1a1a',
      buttonBg: '#555',
      inputBg: '#444',
      danger: '#FF6B6B',
    }
  };

  const colors = themeColors[theme];

  // Helper: hex to RGB
  const hexToRgb = useCallback((hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) }
      : { r: 0, g: 255, b: 0 };
  }, []);

  // Simple seeded random for consistent noise
  const seededRandom = useCallback((x, y, seed) => {
    const n = Math.sin(x * 12.9898 + y * 78.233 + seed * 437.58) * 43758.5453;
    return n - Math.floor(n);
  }, []);

  // Handle foreground image upload
  const handleImageUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setLayers(prev => prev.map(layer => 
        layer.id === 'fg' ? { ...layer, image: event.target.result } : layer
      ));
      setActiveLayer('fg');
    };
    reader.readAsDataURL(file);
  }, []);

  // Handle background image upload
  const handleBgImageUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setLayers(prev => prev.map(layer => 
        layer.id === 'bg' ? { ...layer, image: event.target.result } : layer
      ));
      setActiveLayer('bg');
    };
    reader.readAsDataURL(file);
  }, []);

  // Toggle layer visibility
  const toggleLayerVisibility = useCallback((layerId) => {
    setLayers(prev => prev.map(layer => 
      layer.id === layerId ? { ...layer, visible: !layer.visible } : layer
    ));
  }, []);

  // Remove layer image
  const removeLayerImage = useCallback((layerId) => {
    setLayers(prev => prev.map(layer => 
      layer.id === layerId ? { ...layer, image: null } : layer
    ));
    if (transformMode && layerId === 'bg') {
      setTransformMode(false);
    }
  }, [transformMode]);

  // Handle double-click on preview to activate transform mode for BG
  const handlePreviewDoubleClick = useCallback(() => {
    const bgLayer = layers.find(l => l.id === 'bg');
    if (bgLayer?.image) {
      setTransformMode(!transformMode);
      setActiveLayer('bg');
      setActivePanel('background');
    }
  }, [layers, transformMode]);

  // Transform mode handlers
  const handleTransformMouseDown = useCallback((e, handle) => {
    if (!transformMode) return;
    e.stopPropagation();
    e.preventDefault();
    
    const startX = e.clientX;
    const startY = e.clientY;
    
    setDragStart({ x: startX, y: startY });
    setInitialTransform({
      bgScale,
      bgRotation,
      bgPositionX,
      bgPositionY,
    });
    
    if (handle === 'move') {
      setIsDragging(true);
    } else if (handle.includes('scale')) {
      setIsScaling(true);
      setTransformHandle(handle);
    } else if (handle === 'rotate') {
      setIsRotating(true);
    }
  }, [transformMode, bgScale, bgRotation, bgPositionX, bgPositionY]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging && !isScaling && !isRotating) return;
      
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;
      
      if (isDragging) {
        const sensitivity = 0.2;
        setBgPositionX(Math.max(0, Math.min(100, initialTransform.bgPositionX + deltaX * sensitivity)));
        setBgPositionY(Math.max(0, Math.min(100, initialTransform.bgPositionY + deltaY * sensitivity)));
      }
      
      if (isScaling) {
        const scaleSensitivity = 0.3;
        let scaleDelta = 0;
        
        if (transformHandle === 'scale-tr' || transformHandle === 'scale-br') {
          scaleDelta = deltaX * scaleSensitivity;
        } else if (transformHandle === 'scale-tl' || transformHandle === 'scale-bl') {
          scaleDelta = -deltaX * scaleSensitivity;
        }
        
        if (transformHandle === 'scale-br' || transformHandle === 'scale-bl') {
          scaleDelta = deltaY * scaleSensitivity;
        } else if (transformHandle === 'scale-tr' || transformHandle === 'scale-tl') {
          scaleDelta = -deltaY * scaleSensitivity;
        }
        
        setBgScale(Math.max(10, Math.min(300, initialTransform.bgScale + scaleDelta)));
      }
      
      if (isRotating) {
        const rotateSensitivity = 0.3;
        setBgRotation(initialTransform.bgRotation + deltaX * rotateSensitivity);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsScaling(false);
      setIsRotating(false);
      setTransformHandle(null);
    };

    if (isDragging || isScaling || isRotating) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isScaling, isRotating, dragStart, initialTransform, transformHandle]);

  // EYEDROPPER: Sample color from preview canvas
  const handleEyedropperClick = useCallback((e) => {
    if (!eyedropperActive || !previewCanvasRef.current) return;
    
    const canvas = previewCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = Math.floor((e.clientX - rect.left) * scaleX);
    const y = Math.floor((e.clientY - rect.top) * scaleY);
    
    if (x >= 0 && x < canvas.width && y >= 0 && y < canvas.height) {
      const ctx = canvas.getContext('2d');
      const pixel = ctx.getImageData(x, y, 1, 1).data;
      const hex = '#' + [pixel[0], pixel[1], pixel[2]]
        .map(v => v.toString(16).padStart(2, '0'))
        .join('');
      
      setSampledColor(hex);
      setKeyColor(hex);
      setEyedropperPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      
      if (!e.shiftKey) {
        setEyedropperActive(false);
      }
    }
  }, [eyedropperActive]);

  // Track mouse movement for magnifier
  const handleMouseMove = useCallback((e) => {
    if (!eyedropperActive || !previewCanvasRef.current) return;
    const rect = previewCanvasRef.current.getBoundingClientRect();
    setEyedropperPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  }, [eyedropperActive]);

  // Keyboard shortcut for eyedropper
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'i' && !e.ctrlKey && !e.metaKey && !e.target.matches('input')) {
        setEyedropperActive(prev => !prev);
      }
      if (e.key === 'Escape') {
        setTransformMode(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Calculate matte
  const calculateMatte = useCallback((r, g, b, keyR, keyG, keyB) => {
    const dr = r - keyR;
    const dg = g - keyG;
    const db = b - keyB;
    
    const distance = Math.sqrt(dr * dr + dg * dg + db * db);
    const normalizedDistance = distance / 441.67;
    
    const gain = screenGain / 100;
    let matte = 1 - Math.min(normalizedDistance * gain * 2, 1);
    
    const balance = screenBalance / 100;
    const channelDistance = Math.abs(
      (r * balance + g * (1 - balance)) - 
      (keyR * balance + keyG * (1 - balance))
    ) / 255;
    
    matte = matte * (1 - channelDistance * gain);
    
    const cb = clipBlack / 100;
    const cw = clipWhite / 100;
    
    if (matte < cb) matte = 0;
    else if (matte > cw) matte = 1;
    else matte = (matte - cb) / (cw - cb);
    
    return Math.max(0, Math.min(1, matte));
  }, [screenGain, screenBalance, clipBlack, clipWhite]);

  // Apply edge shrink/expand to matte
  const applyEdgeShrink = useCallback((matteData, width, height, shrinkAmount) => {
    if (shrinkAmount === 0) return matteData;
    
    const result = new Float32Array(matteData);
    const iterations = Math.abs(Math.round(shrinkAmount / 100 * 15));
    
    for (let iter = 0; iter < iterations; iter++) {
      const temp = new Float32Array(result);
      
      for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
          const idx = y * width + x;
          
          if (shrinkAmount > 0) {
            result[idx] = Math.min(
              temp[idx],
              temp[idx - 1],
              temp[idx + 1],
              temp[idx - width],
              temp[idx + width],
              temp[idx - width - 1],
              temp[idx - width + 1],
              temp[idx + width - 1],
              temp[idx + width + 1]
            );
          } else {
            result[idx] = Math.max(
              temp[idx],
              temp[idx - 1],
              temp[idx + 1],
              temp[idx - width],
              temp[idx + width],
              temp[idx - width - 1],
              temp[idx - width + 1],
              temp[idx + width - 1],
              temp[idx + width + 1]
            );
          }
        }
      }
    }
    
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = y * width + x;
        if (result[idx] > 0 && result[idx] < 1) {
          const avg = (
            result[idx - 1] +
            result[idx + 1] +
            result[idx - width] +
            result[idx + width]
          ) / 4;
          result[idx] = result[idx] * 0.7 + avg * 0.3;
        }
      }
    }
    
    return result;
  }, []);

  // Despill algorithm - ensures we remove green spill without adding green
  const despill = useCallback((r, g, b, keyR, keyG, keyB, matte) => {
    const strength = despillBias / 100;
    const spillAmount = matte * strength;
    
    // Don't despill if the pixel isn't being keyed
    if (matte < 0.01) return { r, g, b };
    
    let newR = r, newG = g, newB = b;
    
    if (keyG > keyR && keyG > keyB) {
      // Green screen despill
      const excess = g - Math.max(r, b);
      if (excess > 0) {
        newG = g - excess * spillAmount;
        newR = Math.min(255, r + excess * spillAmount * 0.5);
        newB = Math.min(255, b + excess * spillAmount * 0.5);
      }
    } else if (keyB > keyR && keyB > keyG) {
      const excess = b - Math.max(r, g);
      if (excess > 0) {
        newB = b - excess * spillAmount;
        newR = Math.min(255, r + excess * spillAmount * 0.5);
        newG = Math.min(255, g + excess * spillAmount * 0.5);
      }
    } else if (keyR > keyG && keyR > keyB) {
      const excess = r - Math.max(g, b);
      if (excess > 0) {
        newR = r - excess * spillAmount;
        newG = Math.min(255, g + excess * spillAmount * 0.5);
        newB = Math.min(255, b + excess * spillAmount * 0.5);
      }
    }
    
    return {
      r: Math.max(0, Math.min(255, Math.round(newR))),
      g: Math.max(0, Math.min(255, Math.round(newG))),
      b: Math.max(0, Math.min(255, Math.round(newB))),
    };
  }, [despillBias]);

  // Draw checkerboard pattern
  const drawCheckerboard = useCallback((ctx, width, height, size = 12) => {
    for (let y = 0; y < height; y += size) {
      for (let x = 0; x < width; x += size) {
        const isEven = ((x / size) + (y / size)) % 2 === 0;
        ctx.fillStyle = isEven ? '#CCCCCC' : '#FFFFFF';
        ctx.fillRect(x, y, size, size);
      }
    }
  }, []);

  // Live preview render
  useEffect(() => {
    const fgLayer = layers.find(l => l.id === 'fg');
    if (!fgLayer?.image) return;

    const renderComposite = async () => {
      setIsProcessing(true);
      await new Promise(resolve => requestAnimationFrame(resolve));
      
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      
      const fgImg = new Image();
      fgImg.onload = () => {
        canvas.width = fgImg.width;
        canvas.height = fgImg.height;

        // Load foreground image data for matte calculation
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(fgImg, 0, 0);
        const fgImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const fgData = fgImageData.data;
        const keyRgb = hexToRgb(keyColor);
        
        // Calculate matte from original foreground
        const matteData = new Float32Array(fgData.length / 4);
        let minX = canvas.width, maxX = 0, minY = canvas.height, maxY = 0;
        let keyedPixels = 0;
        
        for (let i = 0; i < fgData.length; i += 4) {
          const r = fgData[i];
          const g = fgData[i + 1];
          const b = fgData[i + 2];
          
          const matte = calculateMatte(r, g, b, keyRgb.r, keyRgb.g, keyRgb.b);
          matteData[i / 4] = matte;
          
          if (matte > 0.01) {
            keyedPixels++;
            const x = (i / 4) % canvas.width;
            const y = Math.floor((i / 4) / canvas.width);
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
        
        // Apply edge processing
        let finalMatte = matteData;
        if (keyedPixels > 0) {
          finalMatte = applyEdgeShrink(matteData, canvas.width, canvas.height, edgeShrink);
          
          if (edgeSoften > 0) {
            const softenRadius = Math.ceil(edgeSoften / 100 * 8);
            const tempMatte = new Float32Array(finalMatte);
            
            for (let y = minY; y <= maxY; y++) {
              for (let x = minX; x <= maxX; x++) {
                const idx = y * canvas.width + x;
                if (tempMatte[idx] > 0 || 
                    (x > 0 && tempMatte[idx - 1] > 0) ||
                    (x < canvas.width - 1 && tempMatte[idx + 1] > 0) ||
                    (y > 0 && tempMatte[idx - canvas.width] > 0) ||
                    (y < canvas.height - 1 && tempMatte[idx + canvas.width] > 0)) {
                  
                  let sum = 0, count = 0;
                  for (let dy = -softenRadius; dy <= softenRadius; dy++) {
                    for (let dx = -softenRadius; dx <= softenRadius; dx++) {
                      const nx = x + dx, ny = y + dy;
                      if (nx >= 0 && nx < canvas.width && ny >= 0 && ny < canvas.height) {
                        const dist = Math.sqrt(dx * dx + dy * dy);
                        const weight = Math.max(0, 1 - dist / (softenRadius + 1));
                        sum += tempMatte[ny * canvas.width + nx] * weight;
                        count += weight;
                      }
                    }
                  }
                  finalMatte[idx] = count > 0 ? sum / count : tempMatte[idx];
                }
              }
            }
          }
        }

        // Prepare the final composite
        const processFinalComposite = () => {
          // Create final canvas
          const finalCanvas = document.createElement('canvas');
          finalCanvas.width = canvas.width;
          finalCanvas.height = canvas.height;
          const finalCtx = finalCanvas.getContext('2d');
          
          // STEP 1: Draw checkerboard as base layer
          drawCheckerboard(finalCtx, canvas.width, canvas.height);
          
          // STEP 2: Draw background image if available
          const bgLayer = layers.find(l => l.id === 'bg');
          if (bgLayer?.image && bgLayer.visible) {
            const bgImg = new Image();
            bgImg.onload = () => {
              finalCtx.save();
              const centerX = canvas.width / 2;
              const centerY = canvas.height / 2;
              const posX = (bgPositionX - 50) / 100 * canvas.width;
              const posY = (bgPositionY - 50) / 100 * canvas.height;
              
              finalCtx.translate(centerX + posX, centerY + posY);
              finalCtx.rotate((bgRotation * Math.PI) / 180);
              
              const scale = bgScale / 100;
              if (bgFlipH) finalCtx.scale(-1, 1);
              if (bgFlipV) finalCtx.scale(1, -1);
              
              finalCtx.globalAlpha = bgOpacity / 100;
              finalCtx.drawImage(bgImg, -bgImg.width * scale / 2, -bgImg.height * scale / 2, bgImg.width * scale, bgImg.height * scale);
              finalCtx.globalAlpha = 1;
              finalCtx.restore();
              
              compositeForegroundAndGlass();
            };
            bgImg.onerror = compositeForegroundAndGlass;
            bgImg.src = bgLayer.image;
          } else {
            compositeForegroundAndGlass();
          }
          
          function compositeForegroundAndGlass() {
            // STEP 3: Create foreground with alpha based on matte
            const fgWithAlpha = ctx.createImageData(canvas.width, canvas.height);
            for (let i = 0; i < fgData.length; i += 4) {
              const matte = finalMatte[i / 4];
              
              // Apply despill to remove green cast
              const despilled = despill(fgData[i], fgData[i + 1], fgData[i + 2], keyRgb.r, keyRgb.g, keyRgb.b, matte);
              
              fgWithAlpha.data[i] = despilled.r;
              fgWithAlpha.data[i + 1] = despilled.g;
              fgWithAlpha.data[i + 2] = despilled.b;
              // Alpha: matte=1 means fully transparent (window), matte=0 means fully opaque (keep FG)
              fgWithAlpha.data[i + 3] = Math.round(255 * (1 - matte));
            }
            
            const fgCanvas = document.createElement('canvas');
            fgCanvas.width = canvas.width;
            fgCanvas.height = canvas.height;
            const fgCtx = fgCanvas.getContext('2d');
            fgCtx.putImageData(fgWithAlpha, 0, 0);
            
            // Draw foreground ON TOP of background+checkerboard
            finalCtx.drawImage(fgCanvas, 0, 0);
            
            // STEP 4: Create and draw glass overlay
            const glassCanvas = document.createElement('canvas');
            glassCanvas.width = canvas.width;
            glassCanvas.height = canvas.height;
            const glassCtx = glassCanvas.getContext('2d');
            
            const glassImageData = glassCtx.createImageData(canvas.width, canvas.height);
            const target = hexToRgb(replaceColor);
            const glassAlpha = glassOpacity / 100;
            
            for (let i = 0; i < fgData.length; i += 4) {
              const matte = finalMatte[i / 4];
              const idx = i / 4;
              const x = idx % canvas.width;
              const y = Math.floor(idx / canvas.width);
              
              if (matte > 0.005) {
                // Window area - create glass
                let glassR = target.r;
                let glassG = target.g;
                let glassB = target.b;
                
                // Add noise/texture
                const noiseScaleFactor = noiseScale / 100;
                const largeNoise = (seededRandom(Math.floor(x / (3 + noiseScaleFactor * 8)), Math.floor(y / (3 + noiseScaleFactor * 8)), 2) - 0.5) * 2;
                const smallNoise = (seededRandom(x * 2, y * 2, 3) - 0.5) * 2;
                const combinedNoise = largeNoise * 0.5 + smallNoise * 0.5;
                const noiseValue = combinedNoise * (noiseAmount / 100) * 25;
                
                glassR = Math.max(0, Math.min(255, glassR + noiseValue));
                glassG = Math.max(0, Math.min(255, glassG + noiseValue));
                glassB = Math.max(0, Math.min(255, glassB + noiseValue));
                
                glassImageData.data[i] = glassR;
                glassImageData.data[i + 1] = glassG;
                glassImageData.data[i + 2] = glassB;
                glassImageData.data[i + 3] = Math.round(255 * glassAlpha);
              } else {
                // Not a window - no glass
                glassImageData.data[i + 3] = 0;
              }
            }
            
            // Apply gradient and reflection effects to glass
            if (keyedPixels > 0) {
              applyGlassEffects(glassImageData, finalMatte, canvas.width, canvas.height, minX, maxX, minY, maxY);
            }
            
            glassCtx.putImageData(glassImageData, 0, 0);
            
            // Draw glass ON TOP of everything
            finalCtx.drawImage(glassCanvas, 0, 0);
            
            // Update preview
            if (previewCanvasRef.current) {
              const previewCtx = previewCanvasRef.current.getContext('2d');
              previewCanvasRef.current.width = canvas.width;
              previewCanvasRef.current.height = canvas.height;
              previewCtx.drawImage(finalCanvas, 0, 0);
            }
            
            setIsProcessing(false);
          }
        };
        
        processFinalComposite();
      };
      fgImg.src = fgLayer.image;
    };

    // Apply glass effects (gradient + reflection lines)
    const applyGlassEffects = (glassImageData, matteData, width, height, minX, maxX, minY, maxY) => {
      const padding = 15;
      minX = Math.max(0, minX - padding);
      maxX = Math.min(width - 1, maxX + padding);
      minY = Math.max(0, minY - padding);
      maxY = Math.min(height - 1, maxY + padding);
      
      const gradHeight = maxY - minY;
      if (gradHeight <= 0) return;
      
      const strength = gradientStrength / 100;
      const reflectOpacity = reflectionOpacity / 100;
      
      for (let y = minY; y <= maxY; y++) {
        for (let x = minX; x <= maxX; x++) {
          const idx = y * width + x;
          if (matteData[idx] > 0.02 && glassImageData.data[idx * 4 + 3] > 0) {
            const dataIdx = idx * 4;
            
            // Calculate gradient position (0 = top, 1 = bottom)
            const gradientPos = (y - minY) / gradHeight;
            
            // Create realistic glass gradient
            const topBrightness = 0.3 + strength * 0.4;
            const bottomBrightness = -0.1 - strength * 0.3;
            
            let gradientValue;
            if (gradientPos < 0.5) {
              gradientValue = topBrightness * (1 - gradientPos * 2) + (gradientPos * 2) * 0.1;
            } else {
              gradientValue = 0.1 + bottomBrightness * ((gradientPos - 0.5) * 2);
            }
            
            // Add reflection lines
            let reflectionLine = 0;
            
            // Main reflection line at 1/3 from top
            const mainReflectionPos = 0.33;
            const mainReflectionDist = Math.abs(gradientPos - mainReflectionPos);
            if (mainReflectionDist < 0.08) {
              reflectionLine = Math.max(reflectionLine, (1 - mainReflectionDist / 0.08) * reflectOpacity * 1.5);
            }
            
            // Secondary reflection line at 2/3
            const secondReflectionPos = 0.66;
            const secondReflectionDist = Math.abs(gradientPos - secondReflectionPos);
            if (secondReflectionDist < 0.05) {
              reflectionLine = Math.max(reflectionLine, (1 - secondReflectionDist / 0.05) * reflectOpacity * 1.0);
            }
            
            // Subtle reflection at top
            if (gradientPos < 0.15) {
              reflectionLine = Math.max(reflectionLine, (1 - gradientPos / 0.15) * reflectOpacity * 0.8);
            }
            
            // Diagonal reflection patterns
            const diagonalPattern = Math.sin(x * 0.04 + y * 0.02) * 0.5 + 0.5;
            if (gradientPos > 0.2 && gradientPos < 0.8) {
              const diagReflection = diagonalPattern * reflectOpacity * 0.3;
              reflectionLine += diagReflection;
            }
            
            // Horizontal variation
            const hWave = Math.sin(x * 0.01 + 1.5) * 0.5 + 0.5;
            const hReflection = hWave * reflectOpacity * 0.2;
            reflectionLine += hReflection;
            
            // Combine gradient and reflections
            const totalEffect = gradientValue * 80 + reflectionLine * 200;
            
            // Apply to glass pixels
            const currentR = glassImageData.data[dataIdx];
            const currentG = glassImageData.data[dataIdx + 1];
            const currentB = glassImageData.data[dataIdx + 2];
            
            // Add tint
            const tintValue = glassTint / 100;
            const tintR = tintValue * 15;
            const tintB = -tintValue * 15;
            
            const newR = Math.max(0, Math.min(255, currentR + totalEffect + tintR));
            const newG = Math.max(0, Math.min(255, currentG + totalEffect));
            const newB = Math.max(0, Math.min(255, currentB + totalEffect + tintB));
            
            glassImageData.data[dataIdx] = newR;
            glassImageData.data[dataIdx + 1] = newG;
            glassImageData.data[dataIdx + 2] = newB;
          }
        }
      }
    };

    const debounceTimer = setTimeout(renderComposite, 100);
    return () => clearTimeout(debounceTimer);
  }, [
    layers, keyColor, screenGain, screenBalance, clipBlack, clipWhite,
    despillBias, edgeSoften, edgeShrink, replaceColor, glassOpacity, gradientStrength, 
    reflectionOpacity, glassTint, noiseAmount, noiseScale,
    bgScale, bgRotation, bgFlipH, bgFlipV, bgPositionX, bgPositionY,
    bgPerspective, bgOpacity, bgSkewX, bgSkewY, hexToRgb, calculateMatte, 
    despill, applyEdgeShrink, seededRandom, drawCheckerboard
  ]);

  // Close color pickers when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (keyColorPickerRef.current && !keyColorPickerRef.current.contains(event.target)) {
        setShowKeyColorPicker(false);
      }
      if (replaceColorPickerRef.current && !replaceColorPickerRef.current.contains(event.target)) {
        setShowReplaceColorPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Export
  const exportImage = useCallback((format = 'png') => {
    if (!previewCanvasRef.current) return;
    const mimeType = format === 'jpg' ? 'image/jpeg' : 'image/png';
    const quality = format === 'jpg' ? 0.95 : undefined;
    const dataUrl = previewCanvasRef.current.toDataURL(mimeType, quality);
    const link = document.createElement('a');
    link.download = `windowlens-${Date.now()}.${format}`;
    link.href = dataUrl;
    link.click();
  }, []);

  // Reset
  const handleReset = useCallback(() => {
    setKeyColor('#00ff00');
    setScreenGain(100);
    setScreenBalance(50);
    setClipBlack(5);
    setClipWhite(95);
    setDespillBias(70);
    setEdgeSoften(3);
    setEdgeShrink(0);
    setReplaceColor('#d0d0d0');
    setGlassOpacity(65);
    setGradientStrength(50);
    setReflectionOpacity(25);
    setGlassTint(0);
    setNoiseAmount(15);
    setNoiseScale(50);
    setBgScale(100);
    setBgRotation(0);
    setBgFlipH(false);
    setBgFlipV(false);
    setBgPositionX(50);
    setBgPositionY(50);
    setBgPerspective(0);
    setBgOpacity(100);
    setBgSkewX(0);
    setBgSkewY(0);
    setTransformMode(false);
  }, []);

  // Color Swatch component with eyedropper
  const ColorSwatch = ({ color, onClick, label, onEyedropper, eyedropperActive, isKeyColor }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ position: 'relative' }}>
        <button
          onClick={onClick}
          style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            background: color,
            border: `2px solid ${eyedropperActive && isKeyColor ? colors.accent : colors.border}`,
            cursor: 'pointer',
            flexShrink: 0,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            transition: 'all 0.2s ease',
            position: 'relative',
          }}
        >
          {eyedropperActive && isKeyColor && (
            <div style={{
              position: 'absolute',
              inset: -4,
              borderRadius: 14,
              border: `2px dashed ${colors.accent}`,
              animation: 'spin 3s linear infinite',
            }} />
          )}
        </button>
        {isKeyColor && (
          <button
            onClick={onEyedropper}
            style={{
              position: 'absolute',
              bottom: -4,
              right: -4,
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: eyedropperActive ? colors.accent : colors.buttonBg,
              border: `1.5px solid ${colors.border}`,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
              zIndex: 1,
            }}
            title="Eyedropper tool (press I)"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={eyedropperActive ? 'white' : colors.text} strokeWidth="2">
              <path d="M2 22l1-1h3l9-9" />
              <path d="M12.9 3.1L11 5l3 3 1.9-1.9a2.12 2.12 0 0 0-3-3z" />
              <path d="M10.3 13L2 22" />
            </svg>
          </button>
        )}
      </div>
      <div>
        <div style={{ fontSize: '0.8rem', fontWeight: 500, color: colors.text }}>
          {label}
        </div>
        <div style={{ fontSize: '0.7rem', color: colors.textSecondary, fontFamily: 'monospace' }}>
          {color}
        </div>
        {sampledColor && isKeyColor && (
          <div style={{ fontSize: '0.6rem', color: colors.accent, marginTop: 1 }}>
            ✓ Sampled
          </div>
        )}
      </div>
    </div>
  );

  // Slider component
  const SliderControl = ({ label, value, onChange, min = 0, max = 100, unit = '%', leftLabel = '', rightLabel = '' }) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 4,
      }}>
        <label style={{ fontSize: '0.7rem', fontWeight: 500, color: colors.text }}>
          {label}
        </label>
        <span style={{
          fontSize: '0.62rem',
          fontWeight: 600,
          color: colors.accent,
          background: `${colors.accent}20`,
          padding: '1px 5px',
          borderRadius: 4,
          fontFamily: 'monospace',
        }}>
          {value}{unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{
          width: '100%',
          height: 4,
          borderRadius: 2,
          background: `linear-gradient(to right, ${colors.accent} 0%, ${colors.accent} ${(value - min) / (max - min) * 100}%, ${colors.border} ${(value - min) / (max - min) * 100}%, ${colors.border} 100%)`,
          WebkitAppearance: 'none',
          appearance: 'none',
          outline: 'none',
          cursor: 'pointer',
        }}
      />
      {(leftLabel || rightLabel) && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '0.55rem',
          color: colors.textSecondary,
          marginTop: 1,
        }}>
          <span>{leftLabel}</span>
          <span>{rightLabel}</span>
        </div>
      )}
    </div>
  );

  const fgLayer = layers.find(l => l.id === 'fg');
  const bgLayer = layers.find(l => l.id === 'bg');

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: colors.bg,
      color: colors.text,
    }}>
      {/* Top Bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 14px',
        background: colors.surface,
        borderBottom: `1px solid ${colors.border}`,
        zIndex: 20,
        flexShrink: 0,
        minHeight: 46,
        gap: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 700, fontSize: '0.9rem', color: colors.accent, letterSpacing: '-0.3px' }}>
            WindowLens
          </span>
          
          <div style={{ display: 'flex', gap: 3 }}>
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                padding: '4px 10px',
                border: 'none',
                borderRadius: 5,
                background: colors.accent,
                color: 'white',
                cursor: 'pointer',
                fontSize: '0.65rem',
                fontWeight: 500,
              }}
            >
              + FG
            </button>
            <button
              onClick={() => bgFileInputRef.current?.click()}
              style={{
                padding: '4px 10px',
                border: `1px solid ${colors.border}`,
                borderRadius: 5,
                background: colors.buttonBg,
                color: colors.text,
                cursor: 'pointer',
                fontSize: '0.65rem',
              }}
            >
              + BG
            </button>
          </div>

          <input type="file" accept="image/*" ref={fileInputRef} onChange={handleImageUpload} style={{ display: 'none' }} />
          <input type="file" accept="image/*" ref={bgFileInputRef} onChange={handleBgImageUpload} style={{ display: 'none' }} />
          
          {transformMode && (
            <div style={{
              fontSize: '0.6rem',
              padding: '2px 6px',
              background: `${colors.accent}15`,
              borderRadius: 4,
              color: colors.accent,
              fontWeight: 500,
            }}>
              Transform Mode (Esc)
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          {fgLayer?.image && (
            <>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '2px 7px',
                borderRadius: 5,
                background: colors.buttonBg,
                fontSize: '0.6rem',
              }}>
                <div style={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  background: isProcessing ? '#FFB74D' : '#4CAF50',
                }} />
                {isProcessing ? 'Rendering' : 'Live'}
              </div>
              <button onClick={() => exportImage('png')} style={{ padding: '4px 8px', border: `1px solid ${colors.border}`, borderRadius: 5, background: colors.buttonBg, color: colors.text, cursor: 'pointer', fontSize: '0.6rem' }}>PNG</button>
              <button onClick={() => exportImage('jpg')} style={{ padding: '4px 8px', border: `1px solid ${colors.border}`, borderRadius: 5, background: colors.buttonBg, color: colors.text, cursor: 'pointer', fontSize: '0.6rem' }}>JPG</button>
            </>
          )}
          <button onClick={() => setShowProperties(!showProperties)} style={{
            padding: '4px 8px', border: `1.5px solid ${showProperties ? colors.accent : 'transparent'}`, borderRadius: 5,
            background: showProperties ? `${colors.accent}15` : colors.buttonBg, color: showProperties ? colors.accent : colors.text, cursor: 'pointer', fontSize: '0.6rem',
          }}>{showProperties ? '◀' : '▶'}</button>
          <button onClick={handleReset} style={{ padding: '4px 8px', border: `1px solid ${colors.border}`, borderRadius: 5, background: colors.buttonBg, color: colors.text, cursor: 'pointer', fontSize: '0.6rem' }}>↺</button>
        </div>
      </div>

      {/* Eyedropper banner */}
      {eyedropperActive && (
        <div style={{ background: colors.accent, color: 'white', padding: '5px 14px', fontSize: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, zIndex: 19 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 22l1-1h3l9-9" /><path d="M12.9 3.1L11 5l3 3 1.9-1.9a2.12 2.12 0 0 0-3-3z" /></svg>
          Click to sample key color • <strong>I</strong> to toggle
        </div>
      )}

      {/* Main Content */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {showProperties && (
          <div style={{ width: 280, background: colors.surface, borderRight: `1px solid ${colors.border}`, display: 'flex', flexDirection: 'column', zIndex: 10 }}>
            <div style={{ display: 'flex', borderBottom: `1px solid ${colors.border}`, flexShrink: 0 }}>
              {[
                { id: 'keying', label: '🔑 Key' },
                { id: 'glass', label: '🪟 Glass' },
                { id: 'layers', label: '📑 Layers' },
                { id: 'background', label: '🏙️ BG', disabled: !bgLayer?.image },
              ].map(tab => (
                <button key={tab.id} onClick={() => !tab.disabled && setActivePanel(tab.id)} disabled={tab.disabled} style={{
                  flex: 1, padding: '7px 3px', border: 'none',
                  borderBottom: `2px solid ${activePanel === tab.id ? colors.accent : 'transparent'}`,
                  background: activePanel === tab.id ? `${colors.accent}08` : 'transparent',
                  color: tab.disabled ? colors.textSecondary : (activePanel === tab.id ? colors.accent : colors.text),
                  cursor: tab.disabled ? 'not-allowed' : 'pointer', fontSize: '0.58rem',
                  fontWeight: activePanel === tab.id ? 600 : 400, opacity: tab.disabled ? 0.35 : 1,
                  transition: 'all 0.15s ease',
                }}>{tab.label}</button>
              ))}
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
              {activePanel === 'keying' && (
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.4px', color: colors.textSecondary, marginBottom: 10 }}>Screen Color</div>
                  <div ref={keyColorPickerRef} style={{ marginBottom: 16 }}>
                    <ColorSwatch color={keyColor} onClick={() => setShowKeyColorPicker(!showKeyColorPicker)} onEyedropper={() => setEyedropperActive(!eyedropperActive)} eyedropperActive={eyedropperActive} isKeyColor={true} label="Key Color" />
                    {showKeyColorPicker && (
                      <div style={{ marginTop: 6, padding: 8, background: colors.inputBg, borderRadius: 8, border: `1px solid ${colors.border}` }}>
                        <HexColorPicker color={keyColor} onChange={setKeyColor} style={{ width: '100%', height: 120 }} />
                        <div style={{ display: 'flex', gap: 3, marginTop: 5 }}>
                          {['#00ff00', '#00b140', '#39ff14', '#7fff00', '#0000ff', '#4169e1'].map(preset => (
                            <button key={preset} onClick={() => setKeyColor(preset)} style={{ flex: 1, height: 20, borderRadius: 4, background: preset, border: `1.5px solid ${keyColor === preset ? colors.accent : 'transparent'}`, cursor: 'pointer' }} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ fontWeight: 600, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.4px', color: colors.textSecondary, marginBottom: 8, marginTop: 14 }}>Matte Generation</div>
                  <SliderControl label="Screen Gain" value={screenGain} onChange={setScreenGain} leftLabel="Tight" rightLabel="Wide" />
                  <SliderControl label="Screen Balance" value={screenBalance} onChange={setScreenBalance} leftLabel="R/B" rightLabel="Green" />
                  <div style={{ fontWeight: 600, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.4px', color: colors.textSecondary, marginBottom: 8, marginTop: 14 }}>Matte Cleanup</div>
                  <SliderControl label="Clip Black" value={clipBlack} onChange={setClipBlack} />
                  <SliderControl label="Clip White" value={clipWhite} onChange={setClipWhite} />
                  <SliderControl label="Edge Soften" value={edgeSoften} onChange={setEdgeSoften} leftLabel="Sharp" rightLabel="Soft" />
                  <div style={{ marginTop: 4, padding: '8px 10px', background: `${colors.accent}10`, borderRadius: 6, border: `1px solid ${colors.accent}30`, marginBottom: 12 }}>
                    <SliderControl label="Edge Shrink/Expand" value={edgeShrink} onChange={setEdgeShrink} min={-100} max={100} unit="px" leftLabel="Expand" rightLabel="Shrink" />
                    <div style={{ fontSize: '0.55rem', color: colors.textSecondary, marginTop: 2 }}>Negative: expand • Positive: shrink</div>
                  </div>
                  <div style={{ fontWeight: 600, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.4px', color: colors.textSecondary, marginBottom: 8, marginTop: 14 }}>Color Treatment</div>
                  <SliderControl label="Despill Bias" value={despillBias} onChange={setDespillBias} leftLabel="Natural" rightLabel="Aggressive" />
                </div>
              )}
              {activePanel === 'glass' && (
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.4px', color: colors.textSecondary, marginBottom: 10 }}>Glass Color</div>
                  <div ref={replaceColorPickerRef} style={{ marginBottom: 14 }}>
                    <ColorSwatch color={replaceColor} onClick={() => setShowReplaceColorPicker(!showReplaceColorPicker)} isKeyColor={false} label="Replacement" />
                    {showReplaceColorPicker && (
                      <div style={{ marginTop: 6, padding: 8, background: colors.inputBg, borderRadius: 8, border: `1px solid ${colors.border}` }}>
                        <HexColorPicker color={replaceColor} onChange={setReplaceColor} style={{ width: '100%', height: 120 }} />
                        <div style={{ marginTop: 5 }}>
                          <div style={{ fontSize: '0.55rem', color: colors.textSecondary, marginBottom: 2 }}>Glass presets</div>
                          <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                            {['#d0d0d0', '#c5d5c5', '#b8c5d6', '#d4c5b8', '#e0e0e0', '#c0d0d0', '#a8c8d8', '#d8d0c0'].map(preset => (
                              <button key={preset} onClick={() => setReplaceColor(preset)} style={{ width: 22, height: 22, borderRadius: 4, background: preset, border: `1.5px solid ${replaceColor === preset ? colors.accent : 'transparent'}`, cursor: 'pointer' }} />
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ padding: '8px 10px', background: `${colors.accent}10`, borderRadius: 6, border: `1px solid ${colors.accent}30`, marginBottom: 12 }}>
                    <SliderControl label="Glass Opacity" value={glassOpacity} onChange={setGlassOpacity} leftLabel="Clear" rightLabel="Solid" />
                    <div style={{ fontSize: '0.55rem', color: colors.textSecondary, marginTop: 2 }}>How visible the glass overlay appears</div>
                  </div>
                  <div style={{ fontWeight: 600, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.4px', color: colors.textSecondary, marginBottom: 8, marginTop: 14 }}>Glass Properties</div>
                  <SliderControl label="Gradient" value={gradientStrength} onChange={setGradientStrength} leftLabel="Flat" rightLabel="Deep" />
                  <SliderControl label="Reflections" value={reflectionOpacity} onChange={setReflectionOpacity} leftLabel="None" rightLabel="Full" />
                  <SliderControl label="Tint" value={glassTint} onChange={setGlassTint} min={-50} max={50} leftLabel="Cool" rightLabel="Warm" />
                  <div style={{ marginTop: 4, padding: '8px 10px', background: `${colors.accent}10`, borderRadius: 6, border: `1px solid ${colors.accent}30`, marginBottom: 10 }}>
                    <div style={{ fontSize: '0.6rem', fontWeight: 600, color: colors.accent, marginBottom: 6 }}>🏗️ Glass Texture</div>
                    <SliderControl label="Noise Amount" value={noiseAmount} onChange={setNoiseAmount} leftLabel="Clean" rightLabel="Grainy" />
                    <SliderControl label="Noise Scale" value={noiseScale} onChange={setNoiseScale} leftLabel="Fine" rightLabel="Coarse" />
                    <div style={{ fontSize: '0.55rem', color: colors.textSecondary, marginTop: 2 }}>Adds dust/specs to glass surface</div>
                  </div>
                </div>
              )}
              {activePanel === 'layers' && (
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.4px', color: colors.textSecondary, marginBottom: 10 }}>Layer Stack</div>
                  {layers.map((layer, index) => (
                    <div key={layer.id} onClick={() => setActiveLayer(layer.id)} style={{
                      padding: '10px', borderRadius: 8, marginBottom: 5,
                      background: activeLayer === layer.id ? `${colors.accent}10` : colors.buttonBg,
                      border: `1px solid ${activeLayer === layer.id ? colors.accent : colors.border}`,
                      cursor: 'pointer', transition: 'all 0.15s ease',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: layer.image ? '#4CAF50' : '#CCC' }} />
                          <span style={{ fontWeight: 500, fontSize: '0.72rem' }}>{layer.name}</span>
                        </div>
                        <span style={{ fontSize: '0.58rem', color: colors.textSecondary, background: colors.bg, padding: '1px 4px', borderRadius: 3 }}>{index === 0 ? 'Back' : 'Front'}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 3 }}>
                        <button onClick={(e) => { e.stopPropagation(); toggleLayerVisibility(layer.id); }} style={{
                          flex: 1, padding: '3px 5px', border: `1px solid ${colors.border}`, borderRadius: 4,
                          background: layer.visible ? `${colors.accent}15` : 'transparent', color: layer.visible ? colors.accent : colors.textSecondary, cursor: 'pointer', fontSize: '0.58rem',
                        }}>{layer.visible ? '👁' : '—'}</button>
                        {layer.image && (
                          <button onClick={(e) => { e.stopPropagation(); removeLayerImage(layer.id); }} style={{
                            padding: '3px 5px', border: `1px solid ${colors.border}`, borderRadius: 4,
                            background: 'transparent', color: colors.danger, cursor: 'pointer', fontSize: '0.58rem',
                          }}>✕</button>
                        )}
                        {layer.id === 'bg' && layer.image && (
                          <button onClick={(e) => { e.stopPropagation(); setTransformMode(!transformMode); setActivePanel('background'); }} style={{
                            padding: '3px 5px', border: `1px solid ${transformMode ? colors.accent : colors.border}`, borderRadius: 4,
                            background: transformMode ? `${colors.accent}15` : 'transparent', color: transformMode ? colors.accent : colors.text, cursor: 'pointer', fontSize: '0.58rem',
                          }} title="Transform tool">⤡</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {activePanel === 'background' && bgLayer?.image && (
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.4px', color: colors.textSecondary, marginBottom: 8 }}>Transform</div>
                  <SliderControl label="Pos X" value={bgPositionX} onChange={setBgPositionX} leftLabel="L" rightLabel="R" />
                  <SliderControl label="Pos Y" value={bgPositionY} onChange={setBgPositionY} leftLabel="U" rightLabel="D" />
                  <SliderControl label="Scale" value={bgScale} onChange={setBgScale} min={10} max={300} leftLabel="10%" rightLabel="300%" />
                  <SliderControl label="Rotation" value={bgRotation} onChange={setBgRotation} min={-180} max={180} unit="°" />
                  <SliderControl label="Opacity" value={bgOpacity} onChange={setBgOpacity} />
                  <div style={{ marginBottom: 10, display: 'flex', gap: 4 }}>
                    <button onClick={() => setBgFlipH(!bgFlipH)} style={{ flex: 1, padding: '5px', border: `1px solid ${bgFlipH ? colors.accent : colors.border}`, borderRadius: 5, background: bgFlipH ? `${colors.accent}15` : 'transparent', color: bgFlipH ? colors.accent : colors.text, cursor: 'pointer', fontSize: '0.6rem' }}>↔ Flip H</button>
                    <button onClick={() => setBgFlipV(!bgFlipV)} style={{ flex: 1, padding: '5px', border: `1px solid ${bgFlipV ? colors.accent : colors.border}`, borderRadius: 5, background: bgFlipV ? `${colors.accent}15` : 'transparent', color: bgFlipV ? colors.accent : colors.text, cursor: 'pointer', fontSize: '0.6rem' }}>↕ Flip V</button>
                  </div>
                  <button onClick={() => setTransformMode(!transformMode)} style={{ width: '100%', padding: '8px', border: `1px solid ${transformMode ? colors.accent : colors.border}`, borderRadius: 6, background: transformMode ? `${colors.accent}15` : colors.buttonBg, color: transformMode ? colors.accent : colors.text, cursor: 'pointer', fontSize: '0.65rem', fontWeight: 500, marginTop: 8 }}>
                    {transformMode ? '✓ Exit Transform' : '🖱️ Enter Transform'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Canvas Area */}
        <div ref={previewContainerRef} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: colors.canvas, position: 'relative', overflow: 'hidden', cursor: eyedropperActive ? 'crosshair' : (transformMode ? 'grab' : 'default') }} onClick={handleEyedropperClick} onMouseMove={handleMouseMove}>
          {fgLayer?.image ? (
            <div style={{ position: 'relative', maxWidth: 'calc(100% - 30px)', maxHeight: 'calc(100% - 30px)', borderRadius: 10, overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.15)', background: theme === 'dark' ? '#2a2a2a' : '#FFFFFF' }} onDoubleClick={handlePreviewDoubleClick}>
              <canvas ref={previewCanvasRef} style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 170px)', objectFit: 'contain', display: 'block' }} />
              
              {transformMode && !eyedropperActive && (
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 15 }}>
                  <div style={{ position: 'absolute', top: '10%', left: '10%', right: '10%', bottom: '10%', border: '2px dashed rgba(136, 183, 255, 0.8)', borderRadius: 4 }}>
                    {['scale-tl', 'scale-tr', 'scale-bl', 'scale-br'].map((handle, i) => (
                      <div key={handle} onMouseDown={(e) => handleTransformMouseDown(e, handle)} style={{
                        position: 'absolute',
                        top: i < 2 ? -6 : undefined, bottom: i >= 2 ? -6 : undefined,
                        left: i % 2 === 0 ? -6 : undefined, right: i % 2 === 1 ? -6 : undefined,
                        width: 12, height: 12, background: colors.accent, border: '2px solid white',
                        borderRadius: 2, cursor: i % 2 === 0 ? 'nwse-resize' : 'nesw-resize', pointerEvents: 'all',
                      }} />
                    ))}
                    <div onMouseDown={(e) => handleTransformMouseDown(e, 'rotate')} style={{ position: 'absolute', top: -30, left: '50%', transform: 'translateX(-50%)', cursor: 'grab', pointerEvents: 'all' }}>
                      <div style={{ width: 2, height: 20, background: colors.accent, margin: '0 auto' }} />
                      <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${colors.accent}`, background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={colors.accent} strokeWidth="2"><path d="M1 4v6h6M3.5 15.5A9 9 0 1 0 2 12" /></svg>
                      </div>
                    </div>
                    <div onMouseDown={(e) => handleTransformMouseDown(e, 'move')} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 30, height: 30, borderRadius: '50%', background: 'rgba(136, 183, 255, 0.3)', border: '2px solid white', cursor: 'move', pointerEvents: 'all', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><polyline points="5 9 2 12 5 15" /><polyline points="9 5 12 2 15 5" /><polyline points="15 19 12 22 9 19" /><polyline points="19 9 22 12 19 15" /><line x1="2" y1="12" x2="22" y2="12" /><line x1="12" y1="2" x2="12" y2="22" /></svg>
                    </div>
                  </div>
                </div>
              )}
              
              {eyedropperActive && previewCanvasRef.current && (
                <div style={{ position: 'absolute', left: eyedropperPosition.x, top: eyedropperPosition.y, pointerEvents: 'none', transform: 'translate(-50%, -50%)', zIndex: 50 }}>
                  <div style={{ width: 70, height: 70, borderRadius: '50%', border: '3px solid white', boxShadow: '0 0 0 2px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.3)', overflow: 'hidden', background: '#333' }}>
                    <canvas ref={(el) => {
                      if (el && previewCanvasRef.current) {
                        const magCtx = el.getContext('2d');
                        el.width = 70; el.height = 70;
                        const scaleX = previewCanvasRef.current.width / previewCanvasRef.current.getBoundingClientRect().width;
                        const scaleY = previewCanvasRef.current.height / previewCanvasRef.current.getBoundingClientRect().height;
                        const sx = eyedropperPosition.x * scaleX - 4;
                        const sy = eyedropperPosition.y * scaleY - 4;
                        magCtx.drawImage(previewCanvasRef.current, sx, sy, 8, 8, 0, 0, 70, 70);
                        magCtx.strokeStyle = 'red'; magCtx.lineWidth = 1;
                        magCtx.beginPath(); magCtx.moveTo(35, 0); magCtx.lineTo(35, 70); magCtx.moveTo(0, 35); magCtx.lineTo(70, 35); magCtx.stroke();
                      }
                    }} style={{ width: 70, height: 70 }} />
                  </div>
                  <div style={{ textAlign: 'center', color: 'white', fontSize: '0.5rem', fontFamily: 'monospace', marginTop: 3, textShadow: '0 1px 3px rgba(0,0,0,0.8)', background: 'rgba(0,0,0,0.7)', padding: '1px 5px', borderRadius: 3 }}>{keyColor}</div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: colors.textSecondary, padding: 30 }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ marginBottom: 10, opacity: 0.5 }}><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
              <div style={{ fontSize: '0.85rem', marginBottom: 4, fontWeight: 500 }}>No foreground image</div>
              <div style={{ fontSize: '0.65rem', opacity: 0.7 }}>Click "+ FG" to import a render</div>
            </div>
          )}
        </div>
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 12px', background: colors.surface, borderTop: `1px solid ${colors.border}`, flexShrink: 0, minHeight: 20, fontSize: '0.5rem', color: colors.textSecondary }}>
        <span>WindowLens</span>
        <div style={{ display: 'flex', gap: 10 }}>
          <span>FG: {fgLayer?.image ? '✓' : '—'}</span>
          <span>BG: {bgLayer?.image ? '✓' : '—'}</span>
          <span>Key: {keyColor}</span>
          <span>Glass: {glassOpacity}%</span>
          {transformMode && <span style={{ color: colors.accent }}>Transform</span>}
        </div>
        <span style={{ color: colors.accent }}>{eyedropperActive ? 'Eyedropper [I]' : transformMode ? 'Transform [Esc]' : 'Press I'}</span>
      </div>
      
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        input[type="range"]::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 12px; height: 12px; background: ${colors.accent}; border-radius: 50%; cursor: pointer; border: 2px solid white; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
        input[type="range"]::-moz-range-thumb { width: 12px; height: 12px; background: ${colors.accent}; border-radius: 50%; cursor: pointer; border: 2px solid white; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
      `}</style>
    </div>
  );
};

export default WindowLens;